import { apiRequest } from '@/shared/lib/api-client';
import { SYNC_MARK_KEY } from '@/shared/lib/query-cache';
import { queryClient as qc } from '@/shared/lib/query-client';
import { useAuthStore } from '@/shared/stores/auth-store';
import { applyTicketCard, debugInvalidate, invalidateServerTruthLists } from './inbox-debug';
import { useOpenTicketStore } from './stores/open-ticket-store';
import type { TicketSummary } from './types';

/**
 * Serviço de reconexão — "o que eu perdi enquanto estive fora?".
 *
 * ## O problema
 *
 * Enquanto o socket está fora do ar os avisos se perdem: ninguém os guarda pra
 * entregar depois. A pergunta é o que fazer ao voltar.
 *
 * A resposta anterior era desconfiar de TUDO: marcar todas as conversas como
 * velhas e conferir cada uma que o atendente abrisse. Três defeitos vieram daí:
 *
 *   1. Pagava-se releitura até do que não mudou (abrir a mesma conversa pela
 *      terceira vez custava três buscas).
 *   2. A conferência chegava atrasada e bagunçava a tela (a conversa abria,
 *      rolava, e meio segundo depois vinham mensagens que faltavam).
 *   3. Era frágil: vivia num `useEffect` da tela de Atendimento. Quem estava no
 *      Dashboard quando a linha voltou não desconfiava de nada — e o painel
 *      mostrava dado velho com toda a confiança até um F5.
 *
 * Aqui a pergunta é uma só e a resposta traz apenas o que mudou, já em formato
 * de cartão. O encaixe usa o MESMO caminho dos avisos ao vivo, então as
 * correções aparecem como mensagens chegando — sem tela de carregamento.
 *
 * ## Por que nasce junto com o socket, e não num componente
 *
 * `SocketProxy.on()` não repete evento passado. Se `connect` disparasse antes
 * de o React registrar o ouvinte, a recuperação inteira se perdia — e é
 * justamente na volta rápida (notebook reaberto, aba retomada) que ele dispara
 * cedo. Instalar aqui, na mesma volta síncrona em que o socket é construído,
 * torna essa corrida impossível: a mensagem do Worker só chega no próximo
 * ciclo. De quebra, funciona em qualquer rota — Dashboard, Canais, Configurações.
 */

interface RespostaMudancas {
  recarregue: boolean;
  tickets?: TicketSummary[];
  agora: string;
}

function lerMarca(): string | null {
  try {
    return localStorage.getItem(SYNC_MARK_KEY);
  } catch {
    return null; // modo privativo / armazenamento cheio → cai no "recarregue"
  }
}

function gravarMarca(agora: string): void {
  try {
    localStorage.setItem(SYNC_MARK_KEY, agora);
  } catch {
    // Sem marca, a próxima reconexão recarrega. Degrada, não quebra.
  }
}

/** O caminho antigo: marca tudo como suspeito. Rede de segurança do delta. */
function desconfiarDeTudo(motivo: string): void {
  void qc.invalidateQueries({ queryKey: ['ticket-messages'] });
  void qc.invalidateQueries({ queryKey: ['ticket'] });
  void debugInvalidate(qc, ['tickets'], 'reconexao', motivo);
}

async function sincronizar(): Promise<void> {
  const marca = lerMarca();
  let r: RespostaMudancas;
  try {
    const qs = marca ? `?desde=${encodeURIComponent(marca)}` : '';
    r = await apiRequest<RespostaMudancas>(`/tickets/changes${qs}`);
  } catch {
    // Servidor fora, rede instável, sessão expirando: sem resposta não há como
    // saber o que mudou. Desconfia de tudo — exatamente o comportamento antigo.
    desconfiarDeTudo('delta_falhou');
    return;
  }

  if (r.recarregue) {
    desconfiarDeTudo(marca ? 'janela_grande' : 'sem_marca');
    gravarMarca(r.agora);
    return;
  }

  const cartoes = r.tickets ?? [];
  const eu = useAuthStore.getState().user;

  if (cartoes.length > 0) {
    if (!eu) {
      // Sem usuário em memória não dá pra decidir em qual lista cada cartão
      // entra (`belongsTo` depende de perfil e departamentos).
      desconfiarDeTudo('sem_usuario');
      gravarMarca(r.agora);
      return;
    }
    const viewer = {
      id: eu.id,
      profile: eu.profile,
      departmentIds: eu.departmentIds,
      support: Boolean(eu.impersonating),
    };
    for (const card of cartoes) {
      applyTicketCard(qc, card, viewer, { origin: 'reconexao', event: 'delta' });
      // Marcar, não buscar: só a conversa que o atendente ABRIR vai ao
      // servidor. A que estiver aberta agora está ativa e busca sozinha.
      void qc.invalidateQueries({ queryKey: ['ticket-messages', card.id] });
      void qc.invalidateQueries({ queryKey: ['ticket', card.id] });
    }
    // Listas com busca ou triagem ligada não são encaixáveis — só o servidor
    // sabe o que casa com o filtro.
    invalidateServerTruthLists(qc, 'reconexao');
  }

  /**
   * A conversa ABERTA confere sempre, mesmo fora da lista de mudanças.
   *
   * O delta enxerga o que mexe no TICKET. Confirmação de entrega e de leitura
   * mexem só na MENSAGEM — o relógio da bolha virar ✓✓ não bumpa nada no
   * ticket. Era o caso relatado: "eu longe da tela, a atendente respondeu, e
   * quando abri as bolhas estavam paradas no relógio".
   *
   * Custa no máximo uma busca (só existe uma conversa aberta), e só quando o
   * socket volta. É seguro chamar mesmo se o cartão já invalidou acima: o
   * TanStack junta buscas concorrentes da mesma chave.
   */
  const aberta = useOpenTicketStore.getState().openTicketId;
  if (aberta !== null) {
    void qc.invalidateQueries({ queryKey: ['ticket-messages', aberta] });
  }

  gravarMarca(r.agora);
}

/**
 * O mínimo do socket que este serviço usa. Tipado por forma, não por classe,
 * pra que o teste possa passar um dublê sem construir um Web Worker.
 */
interface SocketMinimo {
  // biome-ignore lint/suspicious/noExplicitAny: espelha o Listener do SocketProxy
  on: (evento: string, fn: (...args: any[]) => void) => unknown;
  // biome-ignore lint/suspicious/noExplicitAny: idem
  emit: (evento: string, ...args: any[]) => unknown;
}

/**
 * Liga o serviço a um socket. Chamada UMA vez, dentro do `getSocket()`, na
 * mesma volta síncrona da construção — vide o comentário do topo.
 */
export function instalarReconexao(socket: SocketMinimo): void {
  socket.on('connect', () => {
    /**
     * Re-entrar nas salas ANTES de perguntar o que mudou.
     *
     * Ordem importa: se algo acontecer entre a resposta e o re-join, o aviso
     * não chega e a mudança fica fora dos dois caminhos. Entrando primeiro, o
     * pior caso é receber o mesmo fato duas vezes — e encaixar o mesmo cartão
     * duas vezes não faz diferença.
     *
     * As três salas da inbox o servidor já garante sozinho no `connection`;
     * mantidas aqui porque são idempotentes e o piso não custa nada. A da
     * conversa é só do cliente — o servidor não sabe qual está aberta.
     */
    socket.emit('joinInbox', 'active');
    socket.emit('joinInbox', 'pending');
    socket.emit('joinInbox', 'closed');
    const aberta = useOpenTicketStore.getState().openTicketId;
    if (aberta !== null) socket.emit('joinTicket', aberta);

    void sincronizar();
  });
}
